import React, { useState, useEffect } from "react";
import "../../static/css/DashboardSidebar.css";
import "../../../components/static/css/MainLayout.css";
import { Link, useNavigate } from "react-router-dom";
import { service } from "../../../services/AxiosServices";
import { useCrewAuth } from "../../../context/CrewAndSpaceContext";
import Sidebarprojecticon from "../../static/svg/Sidebarprojecticon";
import AccessControl from "../molecules/AccessControl";
// DashboardSidebar component displays the sidebar with 
// links to "knowledge hubs" and "crews", fetches data from the server, 
// and handles navigation based on user roles and access permissions.

interface CrewProps {
  id: number;
  crew_name: string;
  phase?:number
}

interface SpaceProps {
  id: number;
  space_name: string;
  phase?:number
}

interface CrewType {
  id: number;
  title: string;
  description: string;
  phase?:number

}

interface SpaceType {
  id: number;
  title: string;
  description: string;
  phase?:number

}

interface SidebarData {
  [key: string]: {
    title: string;
    links: {
      name?: string;
      to?: string;
      onClick?: () => void;
      className?: string;
      style?: React.CSSProperties;
    }[];
    icon: string;
  };
}

interface MainSidebarProps {
  category?: string;
}

const DashboardSidebar: React.FC<MainSidebarProps> = ({ category }) => {
  const { crews, spaces, setCrews, setSpaces } = useCrewAuth()!;
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [loadingCrews, setLoadingCrews] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchCrews() {
      setLoadingCrews(true);
      try {
        const response = await service<{ crews: CrewProps[] }>(
          "api/get-all-crew",
          "post",
          { project_id: localStorage.getItem("project_id") }
        );
        const formattedCrews: CrewType[] = response.data.crews.map((crew:any) => ({
          id: crew.id,
          title: crew.crew_name || `Crew ${crew.id}`,
          description: crew?.crew_description || "No description available",
          phase:crew?.phase
        }));
        setCrews(formattedCrews);
      } catch (err) {
        console.error("Error fetching crews:", err);
      } finally {
        setLoadingCrews(false);
      }
    }

    async function fetchSpaces() {
      setLoadingSpaces(true);
      try {
        const response = await service<{ response: SpaceProps[] }>(
          "api/get-spaces",
          "post",
          { project_id: localStorage.getItem("project_id") }
        );
        const formattedSpaces: SpaceType[] = response.data.response.map(
          (space:any) => ({
            id: space.id,
            title: space.space_name || `Space ${space.id}`,
            description: space?.space_description || "No description available",
            phase:space?.phase
          })
        );

        setSpaces(formattedSpaces);
      } catch (err) {
        console.error("Error fetching spaces:", err);
      } finally {
        setLoadingSpaces(false);
      }
    }

    fetchCrews();
    fetchSpaces();
  }, []);

  const handleSpace = (id: number, spaceName?: string) => {
    navigate(`/space/${id}`, { state: { spaceName } });
  };

  const handleCrew = (id: number, crewName: string) => {
    navigate(`/edit_crew/${id}`, { state: { crewName } });
  };

  const handleLink = (path: string) => {
    navigate(path);
  };

  const sidebarData: SidebarData = {
    // "ai-training": {
    //   title: "My promptings",
    //   links: [
    //     { name: "Prompting 1", to: "/prompting/1" },
    //     { name: "Prompting 2", to: "/prompting/2" },
    //     { name: " All my promptings", to: "#" },
    //   ],
    //   icon: "fas fa-graduation-cap",
    // },
  };

  return (
    <div className="sidebar d-flex flex-column">
      <div className="p-4 flex-grow-1">
        <ul className="list-unstyled mb-4">
          <AccessControl
            requiredPermissions={[
              { permission: "manage_spaces", types: ["read"] },
            ]}
            renderNoAccess={<></>}
          >
            <li>
              <p className="sidebar-title mb-0">My knowledge hubs</p>
              <p className="sidebar-title-view mt-1">Last visited</p>

              <ul className="sidebar_project_title list-unstyled mb-3">
                {spaces.slice(0, 3).map((space) => (
                  <li className="mb-2" key={space.id}>
                    <a
                      href={`/space/${space.id}`} 
                      className="sidebarproject d-flex justify-content-between text-decoration-none cursor-pointer"
                      onClick={(e) =>{
                        e.preventDefault()
                        handleSpace(space.id, space.title)}
                      } 
                    >
                      {space.title}
                      <span className="ms-2">
                        <Sidebarprojecticon />
                      </span>
                    </a>
                  </li>
                ))}
                <div className="last-link mt-4">
                  <Link
                    to="/knowledge-hubs"
                    className="text-decoration-none cursor-pointer"
                    style={{ color: "inherit", textDecoration: "none" }}
                  >
                    All my knowledge hubs
                  </Link>
                </div>
              </ul>
              <div className="border-bottom mt-2 mb-3"></div>
            </li>
          </AccessControl>
          {/* Access Control for Crews */}
          <AccessControl
            requiredPermissions={[
              //  { permission: "create_crew", types: ["read"],allowedRoles: ['Admin', 'C-Admin'] }
              { permission: "create_crew", types: ["read"] },
            ]}
            renderNoAccess={<></>}
          >
            <li>
              <p className="sidebar-title mb-0">My agentic crews</p>
              <p className="sidebar-title-view mt-1">Last visited</p>

              <ul className="sidebar_project_title list-unstyled mb-3">
                {crews.slice(0, 3).map((crew) => (
                  <li className="mb-2" key={crew.id}>
                    <a
                      href={`/edit_crew/${crew.id}`} 
                      className="sidebarproject d-flex justify-content-between text-decoration-none cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        handleCrew(crew.id, crew.title);
                      }}
                    >
                      {crew.title}
                      <span className="ms-2">
                        <Sidebarprojecticon />
                      </span>
                    </a>
                  </li>
                ))}
                <div className="last-link mt-4">
                  <Link
                    to="/crews"
                    className="text-decoration-none cursor-pointer"
                    style={{ color: "inherit", textDecoration: "none" }}
                  >
                    All my agentic crews
                  </Link>
                </div>
              </ul>
              <div className="border-bottom mt-2 mb-3"></div>
            </li>
          </AccessControl>
          {Object.keys(sidebarData).map((cat) => {
            const activeData = sidebarData[cat];
            return (
              <li key={cat}>
                <p className="sidebar-title mb-0">{activeData.title}</p>
                <p className="sidebar-title-view mt-1">Last visited</p>

                <ul className="sidebar_project_title list-unstyled mb-4">
                  {activeData.links.map((link, index) => (
                    <li key={index}>
                      {index < activeData.links.length - 1 ? (
                        <a
                          className="sidebarproject d-flex justify-content-between text-decoration-none cursor-pointer"
                          onClick={link.onClick}
                        >
                          {link.name}
                          <span className="ms-2">
                            <Sidebarprojecticon />
                          </span>
                        </a>
                      ) : (
                        <div className="last-link">
                          <a
                            onClick={link.onClick}
                            className="text-decoration-none cursor-pointer"
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            {link.name}
                          </a>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="border-bottom mt-2 mb-3"></div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Manage Section */}
      {/* <div className="manage-links px-4 mb-3 mt-auto">
        <div className="Manage_link_text mb-2">
          <Link to={"/knowledge-hubs"}>Manage knowledge hub</Link>
        </div>
        <div className="Manage_link_text mb-2">
          <Link to={"/crews"}>Manage crews</Link>
        </div>
        <div className="Manage_link_text mb-2">
          <a href="#">Manage prompting</a>
        </div>
        <div className="Manage_link_text">
          <Link to={"/configuration"}>Configuration</Link>
        </div>
      </div> */}
    </div>
  );
};

export default DashboardSidebar;
